import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom';
import './index.css'
import reportWebVitals from './reportWebVitals';
import * as THREE from 'three';
import { Stats, Html, Center, OrbitControls, MeshDistortMaterial, CameraShake, Text } from '@react-three/drei';
import { Canvas, extend, useThree, useFrame } from '@react-three/fiber'
import { useLoader } from '@react-three/fiber'
import { useTexture } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'
import { MeshBasicMaterial } from 'three';
import { MeshStandardMaterial } from 'three';
import { BoxGeometry } from 'three';

import {InnerCircle, Particles, Trails} from './ParticleSystems.js';

extend({ EffectComposer, RenderPass, UnrealBloomPass });


function Effect({ children }) {
  const composer = useRef()
  const { scene, gl, size, camera } = useThree();
  const [brightness,setBrightness] = useState(2);
  const aspect = useMemo(() => new THREE.Vector2(size.width, size.height), [size])
  useEffect(() => void composer.current.setSize(size.width, size.height), [size])
  useFrame(({clock}) => {
    composer.current.render();
    if(clock.elapsedTime < 14) setBrightness(2);
    if(clock.elapsedTime > 14.5 && clock.elapsedTime < 15) 
      setBrightness(2 + clock.elapsedTime - 14.5);
    else if(clock.elapsedTime > 18 && clock.elapsedTime < 20)
      setBrightness(2 + (clock.elapsedTime - 18) * (clock.elapsedTime - 18) * 20);
    else if(clock.elapsedTime > 20 && clock.elapsedTime < 25) 
      setBrightness(2 + (clock.elapsedTime - 18) * (clock.elapsedTime - 18) * 20 / Math.pow((clock.elapsedTime - 19), 5));
    else if(clock.elapsedTime > 26 && clock.elapsedTime < 27) setBrightness(2);
  }, 1)
  return (
    <>
    {children}
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachArray="passes" scene={scene} camera={camera} />
      <unrealBloomPass attachArray="passes" args={[aspect, brightness, 0.5, 0]} />
    </effectComposer>
    </>
  )
}


function Sphere()
{
  const texture = useTexture("./noise.png");
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  
  useFrame(({clock}) => {
    texture.offset.y = clock.elapsedTime / 10;
  });

  return(
    <mesh position={[0, 0, 0]}>
      <sphereGeometry attach="geometry" args={[0.35, 16, 16]} />
      <meshBasicMaterial
        attach="material"
        map={texture}
      />
    </mesh>
  )
}

function rand() {
  return Math.random() * 2 - 1;
}

function Orb()
{
  const gltf = useLoader(GLTFLoader, './orb.glb');
  const ref = useRef();
  const lastPos = new THREE.Vector3(0);
  gltf.scene.scale.set(0.5,0.5,0.5);
  gltf.scene.children[0].material = new THREE.MeshPhysicalMaterial({color: "#8888FF", roughness: 0.2, metalness: 0.4});
  
  useFrame(({clock}) => {
    
    if(clock.elapsedTime > 4) {
      ref.current.rotation.x = Math.tan(clock.elapsedTime * 10);
      ref.current.rotation.y = Math.tan(clock.elapsedTime * 10);
      ref.current.rotation.z = Math.tan(clock.elapsedTime * 10);
    }
    else {
      ref.current.rotation.x = Math.sin(clock.elapsedTime/5 * clock.elapsedTime) * Math.PI * 2;
      ref.current.rotation.y = Math.sin(clock.elapsedTime/5 * clock.elapsedTime) * Math.PI * 2;
      ref.current.rotation.z = Math.sin(clock.elapsedTime/5 * clock.elapsedTime) * Math.PI * 2;
    }

    let newPos = lastPos;
    if(Math.random() > 0.95 && clock.elapsedTime > 5) {
      newPos.set(rand(), rand(), rand());
    }
    else if(clock.elapsedTime < 3) {
      ref.current.position.set(0,-4 + clock.elapsedTime * 1.5, 0);
    }
    if(clock.elapsedTime > 4)
      ref.current.position.copy(newPos).multiplyScalar(0.8);

    if(clock.elapsedTime > 14.5 && clock.elapsedTime < 15)
      ref.current.scale.set(15.01 - clock.elapsedTime, 15.01 - clock.elapsedTime, 15.01 - clock.elapsedTime);
    else if(clock.elapsedTime > 18)
      ref.current.scale.set(Math.min(clock.elapsedTime - 17, 0.1), Math.min(clock.elapsedTime - 17, 0.1), Math.min(clock.elapsedTime - 17, 0.1));

    lastPos.copy(ref.current.position);
  });

  return (
    <primitive ref={ref} object={gltf.scene} />
  )
}

function Lights()
{
  const ref = useRef();
  const [shake, setShake] = useState(true);
  const [shakeFreq, setShakeFreq] = useState(0);
  
  useFrame(({clock, camera}) => {
    ref.current.lookAt(camera.position);

    if(clock.elapsedTime > 20 && clock.elapsedTime < 21)  {
      setShake(false);
      setShakeFreq(22 - clock.elapsedTime);
    }
    else if(clock.elapsedTime < 20) setShakeFreq(clock.elapsedTime / 3);

    // ref.current.rotation.z = clock.elapsedTime * 2;
  });

  return (
  <group ref={ref}>
    <ambientLight intensity = {0.1} />
    <pointLight position={[0,0,-4]} intensity={shakeFreq} color={'#12aaFF'}/>
    <pointLight position={[0,0,4]} intensity={shakeFreq} color={'#12aaFF'}/>
    
    <OrbitControls panSpeed={0}/>
    {shake ? <CameraShake maxYaw={0.01} maxPitch={0.01} maxRoll={0.01} yawFrequency={shakeFreq} pitchFrequency={shakeFreq} rollFrequency={shakeFreq}/> : null}
  </group>)
}

let started = false;
function SurroundingSphere({playing}) {
  const ref = useRef();
  const {clock} = useThree();

  useEffect(() => {
    
    if(!playing && !started)
      clock.stop();
    else if(!started) {
      clock.start();
      started = true;
    }
  })

  useFrame(({clock}) => {

    if(clock.elapsedTime < 20) ref.current.scale.set(0,0,0);
    else if(clock.elapsedTime > 18 && clock.elapsedTime < 19)  {
      ref.current.scale.set(clock.elapsedTime - 18, clock.elapsedTime - 18, clock.elapsedTime - 18);
    }
    else ref.current.scale.set(1,1,1)
    

  });

  return (
  <mesh ref={ref}>
    <sphereGeometry args={[0.9]} />
    <MeshDistortMaterial color={"#aaaaaa"} transparent={true} opacity={0.3} side={THREE.BackSide} speed={10} distort={0.3} roughness={0.6} metalness={0.4} />
  </mesh>)
}



function App() {
  let audio = new Audio('./raiser.mp3');
  const [start, setStart] = useState(false);

  return (
    <div id="wrapper" onMouseDown={() => {console.log("PRESSED"); if(!start) {audio.play(); setStart(true)}}}>
    {!start ? <h1 style={{position: 'absolute', top: '50%', left: '50%', zIndex:'100', color:'white', transform:'translate(-50%,-50%)', textAlign:'center'}}>Click anywhere to explode</h1>: null}
    <Canvas>
      <color attach="background" args={["#010306"]} /> {/* Dark  #010306*/} {/* Light #051040*/}
    <Effect >
      <Lights />
      
      <Suspense fallback={null}>
        <Particles count={1000}/>
        <InnerCircle count={30}/>
        <Trails count={1000} />
        {/* <Sphere /> */}
        <Orb />
      </Suspense>

      <SurroundingSphere playing={start}/>

      <mesh position={[0,0,1]} name={'plane'}>
        <planeGeometry args={[1000, 1000]} />
        <meshBasicMaterial color="black" visible={false}/>
      </mesh>
    {/* <Stats /> */}
    </Effect>
  </Canvas>
  </div>
  );
}

export default App;
